/**
 * วิชาตัวเบา (qinggong): movement arts, learned one by one apart from the martial arts. A new
 * character has none of them: only a plain run, a single plain jump and a short dodge step (ก้าวหลบ),
 * which is enough for the combat triangle (a jump clears a ground circle, the dodge step slips a
 * break). Each art opens a way of moving (running on water, up walls, steps on the air, a glide...)
 * and gets better level by level. The rules are in ../qinggong.ts; the design in docs/archive/QINGGONG.md.
 *
 * Han glyphs here must already be in UI_GLYPHS (the display font is subset to them).
 */
import { START, type ZoneId } from './zones';

/** Where and how an art is taught. Every condition given must hold. */
export interface QinggongLearn {
  /** teacher: taught by someone in a zone; book: a manual bought anywhere; quest: earned by the story; school: taught at a school's mountain. */
  readonly how: 'teacher' | 'book' | 'quest' | 'school' | 'drop';
  /** Who teaches it, or what the manual is called (shown in the panel). */
  readonly from: string;
  /** Zones it can be learned in (the teacher's); anywhere when left out. */
  readonly zones?: readonly ZoneId[];
  /** The story quest (index into QUESTS) that must have been reached. */
  readonly questIndex?: number;
  /**
   * The story quest (by id) that must have been handed in, where the chain has it; it wins over `questIndex`, which is
   * only for chains without it (the 72 m flow). The plains chain moved with the first-hour fixes, so it names the quest.
   */
  readonly questId?: string;
  readonly cultivation: number;
  readonly gold?: number;
  /** A dropped art (docs/archive/QINGGONG_DROPS_RED_LANTERN.md): the manual (a good, data/qinggongDrops.ts) that is read to learn it, wherever the player is. */
  readonly manual?: string;
  /** A dropped art: the level a manual alone opens; higher levels need the teacher in `zones`. */
  readonly selfCap?: number;
  /** Goods spent on learning it (the three ตราเมฆา of เหยียบเมฆาเหินฟ้า). */
  readonly seals?: readonly string[];
}

interface QinggongBase {
  readonly id: string;
  readonly name: string;
  readonly glyph: string;
  readonly desc: string;
  /** Other arts needed first, at least at these levels. */
  readonly requires?: Readonly<Record<string, number>>;
  readonly learn: QinggongLearn;
  /** Cultivation to raise level L to L+1 (index L-1); its length + 1 is the top level. */
  readonly train: readonly number[];
  /** What each level gives (index = level - 1), for the panel. */
  readonly notes: readonly string[];
}

/** พุ่งตัว: the dodge on Q, longer and safer than the plain dodge step; from level 3 also in the air. */
export interface DashLevel {
  readonly dist: number;
  readonly stamina: number;
  readonly invuln: number;
  readonly air: boolean;
}
/** วิ่งพันลี้: hold Shift to run faster for stamina per second. */
export interface SprintLevel {
  readonly mult: number;
  readonly stamina: number;
}
/** เท้าผ่านฟ้า: jumps in all (the ground jump included) and the stamina of each air jump. */
export interface SkyStepLevel {
  readonly jumps: number;
  readonly stamina: number;
}
/**
 * ไต่กำแพง: climbing speed (px/s), stamina per second, and the tallest wall it can run up, in metres
 * from the ground at its foot (docs/archive/YANYU.md §9.3: the temple base's 6 m needs level 3; the numbers
 * are still to be balanced with the user).
 */
export interface ClimbLevel {
  readonly speed: number;
  readonly stamina: number;
  readonly maxHeight: number;
}
/** วิ่งบนน้ำ: stamina per second on the water, and the least stamina needed to step onto it. */
export interface WaterLevel {
  readonly stamina: number;
  readonly enter: number;
}
/**
 * ร่อนลม: the fastest fall while gliding (px/s), stamina per second while drifting, and the speed
 * through the air. Drifting is free (the user's decision, 2026-10-07: "ร่อนได้อิสระ"): `stamina` is 0
 * at every level, so a glide lasts until it lands; gliding with Shift still costs the run's rate.
 */
export interface GlideLevel {
  readonly fall: number;
  readonly stamina: number;
  readonly speed: number;
}
/** ข้ามเหว: speed carried through the air from a running jump, and over water in a high jump. */
export interface LeapLevel {
  readonly mult: number;
}
/** เหยียบเมฆาเหินฟ้า: one more air jump and every qinggong stamina cost multiplied by `cost`. */
export interface MasterLevel {
  readonly extraJumps: number;
  readonly cost: number;
}

export type QinggongDef = QinggongBase &
  (
    | { readonly kind: 'dash'; readonly levels: readonly DashLevel[] }
    | { readonly kind: 'sprint'; readonly levels: readonly SprintLevel[] }
    | { readonly kind: 'skyStep'; readonly levels: readonly SkyStepLevel[] }
    | { readonly kind: 'climb'; readonly levels: readonly ClimbLevel[] }
    | { readonly kind: 'water'; readonly levels: readonly WaterLevel[] }
    | { readonly kind: 'glide'; readonly levels: readonly GlideLevel[] }
    | { readonly kind: 'leap'; readonly levels: readonly LeapLevel[] }
    | { readonly kind: 'master'; readonly levels: readonly MasterLevel[] }
  );

export type QinggongKind = QinggongDef['kind'];

/**
 * วิ่งพันลี้ in a walking zone (data/zones.ts `walkSpeed`): the run is this pace times the art's `mult`, in place of
 * the slow walking pace times it (docs/systems/FUN_P1.md section 1): ขั้น 1 = 6 m/s (mult 1.5) up to ขั้น 5 = 8 m/s (mult 2).
 */
export const QG_RUN_BASE = 4;

/** What everyone can do without any art: one jump and a short dodge step (ก้าวหลบ). */
export const QINGGONG_BASE = {
  jumps: 1,
  dash: { dist: 2.2, stamina: 25, invuln: 0.35, air: false } satisfies DashLevel,
} as const;

const list: QinggongDef[] = [
  {
    id: 'swallow-dash',
    kind: 'dash',
    name: 'นกนางแอ่นพุ่งตัว',
    glyph: '衝',
    desc: 'พุ่งตัวไปข้างหน้าเร็วดุจนกนางแอ่น หลบได้ทุกการโจมตีขณะพุ่ง ไกลกว่าก้าวหลบธรรมดา ขั้นสามพุ่งกลางอากาศได้',
    learn: { how: 'teacher', from: 'ครูฝึกเฒ่าหวังแห่งลานฝึก', zones: [START, 'plains'], cultivation: 30 },
    train: [60, 120, 220, 360],
    levels: [
      { dist: 3.5, stamina: 25, invuln: 0.4, air: false },
      { dist: 4, stamina: 25, invuln: 0.4, air: false },
      { dist: 4, stamina: 25, invuln: 0.4, air: true },
      { dist: 4.5, stamina: 22, invuln: 0.45, air: true },
      { dist: 5, stamina: 18, invuln: 0.5, air: true },
    ],
    notes: [
      'พุ่ง 3.5 ช่อง · พลังกาย 25',
      'พุ่ง 4 ช่อง',
      'พุ่งกลางอากาศได้ครั้งละหนึ่งต่อการกระโดด',
      'พุ่ง 4.5 ช่อง · พลังกาย 22 · หลบนานขึ้น',
      'พุ่ง 5 ช่อง · พลังกาย 18',
    ],
  },
  {
    id: 'thousand-li',
    kind: 'sprint',
    name: 'วิ่งพันลี้',
    glyph: '奔',
    desc: 'กด Shift ค้างขณะเดินเพื่อวิ่งเร็วขึ้นมาก ใช้พลังกายต่อวินาที ตั้งรับระหว่างวิ่งไม่ได้ เป็นพื้นฐานของวิชาตัวเบาอื่นๆ',
    learn: { how: 'teacher', from: 'ครูฝึกเฒ่าหวังแห่งลานฝึก', zones: [START, 'plains'], cultivation: 40 },
    train: [80, 150, 260, 400],
    levels: [
      { mult: 1.5, stamina: 16 },
      { mult: 1.65, stamina: 15 },
      { mult: 1.8, stamina: 14 },
      { mult: 1.9, stamina: 12 },
      { mult: 2, stamina: 10 },
    ],
    notes: [
      'เร็วขึ้น 50% · พลังกาย 16/วินาที',
      'เร็วขึ้น 65% · พลังกาย 15/วินาที',
      'เร็วขึ้น 80% · พลังกาย 14/วินาที',
      'เร็วขึ้น 90% · พลังกาย 12/วินาที',
      'เร็วขึ้นเท่าตัว · พลังกาย 10/วินาที',
    ],
  },
  {
    id: 'sky-step',
    kind: 'skyStep',
    name: 'เท้าผ่านฟ้า',
    glyph: '踏',
    desc: 'เหยียบอากาศกระโดดซ้ำกลางอากาศ ขั้นสามก้าวที่สามคือเหยียบเมฆา · ใช้ท่าทำลายกลางอากาศแล้วได้การกระโดดคืน',
    requires: { 'swallow-dash': 1 },
    learn: {
      how: 'quest',
      from: 'นักพรตเท้าเปล่า ณ ศิลาเก้ากระบี่',
      zones: ['plains'],
      questIndex: 2,
      questId: 'plains-stones',
      cultivation: 120,
    },
    train: [180, 300, 450, 650],
    levels: [
      { jumps: 2, stamina: 14 },
      { jumps: 2, stamina: 12 },
      { jumps: 3, stamina: 12 },
      { jumps: 3, stamina: 10 },
      { jumps: 3, stamina: 8 },
    ],
    notes: [
      'กระโดดกลางอากาศได้อีก 1 ครั้ง · พลังกาย 14',
      'พลังกาย 12 ต่อก้าว',
      'กระโดดกลางอากาศได้อีก 2 ครั้ง (ครั้งที่สามคือเหยียบเมฆา)',
      'พลังกาย 10 ต่อก้าว',
      'พลังกาย 8 ต่อก้าว',
    ],
  },
  {
    id: 'wall-run',
    kind: 'climb',
    name: 'ไต่กำแพง',
    glyph: '步',
    desc: 'วิ่งพันลี้เข้าหากำแพง แล้ววิ่งขึ้นไปตามผนังจนเหยียบขึ้นหลังคา ลานยกพื้น หินสูง หรือชะง่อนผาได้ ใช้พลังกายต่อวินาที · กด Space ขณะไต่ ถีบผนังดีดตัวกลับขึ้นฟ้าและได้กระโดดกลางอากาศคืนหนึ่งครั้ง',
    requires: { 'thousand-li': 2 },
    learn: { how: 'drop', from: 'ตำราไต่ผา จากลิงยักษ์ทงปี้ (ขั้น 4 ขึ้นไป ครูฝึกเฒ่าหวัง)', zones: [START, 'plains'], manual: 'qg-manual-wall-run', selfCap: 3, cultivation: 150, gold: 150 },
    train: [200, 320, 480, 700],
    levels: [
      { speed: 120, stamina: 26, maxHeight: 3.6 },
      { speed: 150, stamina: 22, maxHeight: 5.8 },
      { speed: 165, stamina: 19, maxHeight: 8.8 },
      { speed: 180, stamina: 16, maxHeight: 8.8 },
      { speed: 200, stamina: 13, maxHeight: 8.8 },
    ],
    notes: [
      'ขึ้นลานยกพื้นและหินสูงได้ (สูงถึง 3.6 เมตร) · พลังกาย 26/วินาที',
      'ขึ้นหลังคาบ้านและศาลาได้ · พลังกาย 22/วินาที',
      'ขึ้นฐานศาลเจ้าสูง 6 เมตรได้ · ไต่เร็วขึ้น · พลังกาย 19/วินาที',
      'ไต่เร็วขึ้น · พลังกาย 16/วินาที',
      'ไต่เร็วที่สุด · พลังกาย 13/วินาที',
    ],
  },
  {
    id: 'water-skim',
    kind: 'water',
    name: 'วิ่งบนน้ำ',
    glyph: '流',
    desc: 'เหยียบผิวน้ำดุจเหยียบใบบัว เดินและวิ่งบนน้ำได้ตราบที่พลังกายยังเหลือ พลังกายหมดกลางน้ำจะจมแล้วว่ายกลับฝั่ง',
    requires: { 'thousand-li': 2 },
    learn: { how: 'drop', from: 'ตำราวิ่งบนน้ำ จากถ้ำคลื่นคำราม (ขั้น 4 ขึ้นไป ชายชราจับปลา)', zones: [START], manual: 'qg-manual-water-skim', selfCap: 3, cultivation: 200 },
    train: [260, 400, 560, 800],
    levels: [
      { stamina: 22, enter: 30 },
      { stamina: 19, enter: 25 },
      { stamina: 16, enter: 20 },
      { stamina: 13, enter: 15 },
      { stamina: 10, enter: 10 },
    ],
    notes: [
      'พลังกาย 22/วินาทีบนน้ำ · ต้องมีพลังกาย 30 จึงก้าวลงน้ำได้',
      'พลังกาย 19/วินาที · ก้าวลงน้ำเมื่อมี 25',
      'พลังกาย 16/วินาที · ก้าวลงน้ำเมื่อมี 20',
      'พลังกาย 13/วินาที · ก้าวลงน้ำเมื่อมี 15',
      'พลังกาย 10/วินาที · ก้าวลงน้ำเมื่อมี 10',
    ],
  },
  {
    id: 'feather-fall',
    kind: 'glide',
    name: 'ร่อนลมขนนก',
    glyph: '風',
    desc: 'กดกระโดดอีกครั้งเมื่อกระโดดครบแล้ว กางแขนร่อนลงช้าๆ ดุจขนนก ลอยไปได้ไกลจนถึงพื้นโดยไม่ใช้พลังกาย กดอีกครั้งเพื่อหุบ กด Shift ร่อนเร็วขึ้นแต่ใช้พลังกายเท่าวิ่งพันลี้',
    requires: { 'sky-step': 1 },
    learn: { how: 'drop', from: 'ตำราร่อนลมขนนก จากนกเพลิงปี้ฟาง (ขั้น 4 ขึ้นไป ฤๅษีลมหนาวหรือผู้เฒ่าเหยียบเมฆ)', zones: ['snow', START], manual: 'qg-manual-feather-fall', selfCap: 3, cultivation: 250 },
    train: [300, 450, 620, 850],
    levels: [
      { fall: 70, stamina: 0, speed: 1.1 },
      { fall: 60, stamina: 0, speed: 1.15 },
      { fall: 50, stamina: 0, speed: 1.2 },
      { fall: 42, stamina: 0, speed: 1.3 },
      { fall: 35, stamina: 0, speed: 1.35 },
    ],
    notes: [
      'ตกช้าลงเหลือ 70 · ร่อนได้อิสระไม่ใช้พลังกาย',
      'ตกช้าลงเหลือ 60',
      'ตกช้าลงเหลือ 50 · ลอยเร็วขึ้น',
      'ตกช้าลงเหลือ 42 · ลอยเร็วขึ้น',
      'ตกช้าลงเหลือ 35 · ลอยเร็วที่สุด',
    ],
  },
  {
    id: 'gap-leap',
    kind: 'leap',
    name: 'กระโจนข้ามเหว',
    glyph: '躍',
    desc: 'กระโดดขณะวิ่งพันลี้ แรงวิ่งพาตัวพุ่งไกลกลางอากาศ เมื่อลอยสูงพอ ข้ามลำธารและผืนน้ำแคบๆ ได้',
    requires: { 'thousand-li': 2, 'sky-step': 1 },
    learn: { how: 'drop', from: 'ตำรากระโจนข้ามเหว จากเต่าคะนองเหล่ยกุย (ขั้น 4 ขึ้นไป จอมยุทธ์พเนจร)', zones: ['dim-moon', START], manual: 'qg-manual-gap-leap', selfCap: 3, cultivation: 220 },
    train: [280, 420, 600, 820],
    levels: [{ mult: 1.25 }, { mult: 1.35 }, { mult: 1.45 }, { mult: 1.55 }, { mult: 1.65 }],
    notes: [
      'ลอยไปเร็วขึ้น 25% เมื่อกระโดดจากการวิ่ง',
      'เร็วขึ้น 35%',
      'เร็วขึ้น 45%',
      'เร็วขึ้น 55%',
      'เร็วขึ้น 65%',
    ],
  },
  {
    id: 'cloud-ascent',
    kind: 'master',
    name: 'เหยียบเมฆาเหินฟ้า',
    glyph: '雲',
    desc: 'ยอดวิชาตัวเบาแห่งยอดหมอก รวมทุกวิชาเป็นหนึ่ง เหยียบเมฆาได้อีกหนึ่งก้าว และวิชาตัวเบาทุกวิชาใช้พลังกายน้อยลง',
    requires: { 'sky-step': 5, 'wall-run': 3, 'water-skim': 3, 'feather-fall': 3 },
    learn: {
      how: 'school',
      from: 'เจ้าอาวาสหอเมฆาแห่งยอดหมอก',
      zones: ['mist-peak'],
      cultivation: 1200,
      gold: 1000,
      seals: ['qg-seal-wb-ape', 'qg-seal-wb-firebird', 'qg-seal-wb-thunder-turtle'],
    },
    train: [1600, 2400],
    levels: [
      { extraJumps: 1, cost: 0.85 },
      { extraJumps: 1, cost: 0.8 },
      { extraJumps: 1, cost: 0.7 },
    ],
    notes: [
      'กระโดดกลางอากาศได้อีก 1 ครั้ง · พลังกายวิชาตัวเบา ×0.85',
      'พลังกายวิชาตัวเบา ×0.8',
      'พลังกายวิชาตัวเบา ×0.7',
    ],
  },
];

export const QINGGONG_LIST: readonly QinggongDef[] = list;
export const QINGGONG: Readonly<Record<string, QinggongDef>> = Object.fromEntries(list.map((q) => [q.id, q]));

export function getQinggong(id: string): QinggongDef {
  const q = QINGGONG[id];
  if (!q) throw new Error(`Unknown qinggong: ${id}`);
  return q;
}

/** The top level of an art. */
export const qinggongMax = (q: QinggongDef): number => q.levels.length;

/**
 * A teacher in person (docs/archive/QINGGONG.md §7): where they stand (a landmark of their zone's map,
 * shifted by dx, dy), and what they say. Spawned by systems/teachers.ts where the landmark exists.
 */
export interface QinggongTeacher {
  readonly id: string;
  readonly zone: ZoneId;
  readonly at: string;
  readonly dx?: number;
  readonly dy?: number;
  readonly facing?: 1 | -1;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  /** The arts they teach (learned in the qinggong tab while in their zone). */
  readonly teaches: readonly string[];
  readonly text: string;
}

export const QINGGONG_TEACHERS: readonly QinggongTeacher[] = [
  {
    id: 'old-wang',
    zone: START,
    at: 'trainYard',
    name: 'ครูฝึกเฒ่าหวัง',
    title: 'ครูวิชาตัวเบา',
    sprite: 'watchman',
    teaches: ['swallow-dash', 'thousand-li'],
    text:
      'ตัวจะเบาได้ ใจต้องนิ่งก่อน ข้าสอนนกนางแอ่นพุ่งตัวกับวิ่งพันลี้ ' +
      'เปิดแท็บวิชาตัวเบาในหน้าต่างตัวละครแล้วเรียนได้เลย ฝึกบนป่าเสาดอกเหมยกับกำแพงซ้อมทางทิศใต้ของลานฝึกนี่แหละ ' +
      'ส่วนไต่กำแพง ถ้าได้ตำรามาก็อ่านเรียนเองได้ถึงขั้น 3 (ตำราได้จากลิงยักษ์ทงปี้) เอามาให้ข้าดู ข้าพาไปขั้น 4 ขึ้นไป',
  },
  {
    id: 'old-fisherman',
    zone: START,
    at: 'pierEnd',
    facing: -1,
    name: 'ชายชราจับปลา',
    title: 'ครูวิ่งบนน้ำ',
    sprite: 'fisherOld',
    teaches: ['water-skim'],
    text:
      'คลื่นไม่เคยหยุด แต่เท้าคนหยุดบนคลื่นได้ ข้าไม่สอนตั้งแต่ต้นแล้ว ไปหาตำราวิ่งบนน้ำมา (หน้าตำราได้จากถ้ำคลื่นคำราม) ' +
      'อ่านเรียนเองได้ถึงขั้น 3 เอาตำรามาให้ข้าดู ข้าจะพาไปขั้น 4 ขึ้นไป แล้วลองวิ่งไปเกาะหินกลางอ่าวทางทิศตะวันออกเฉียงเหนือดู',
  },
  {
    id: 'wandering-master',
    zone: START,
    at: 'leapMaster',
    name: 'จอมยุทธ์พเนจร',
    title: 'ครูกระโจนข้ามเหว',
    sprite: 'taoist',
    teaches: ['gap-leap'],
    text:
      'ช่องระหว่างผายอดหมอกกับผาหินระฆังกว้างเกินก้าวธรรมดา แต่ไม่เกินกระโจนของคนที่วิ่งพันลี้คล่องและเหยียบอากาศเป็น ' +
      'ตำรากระโจนข้ามเหวอยู่กับเต่าคะนองเหล่ยกุย (หน้าตำราจากบอสโลก) อ่านเรียนเองได้ถึงขั้น 3 เอาตำรามาให้ข้า ข้าพาไปขั้น 4 ขึ้นไป แล้วลองกระโดดขณะวิ่งที่ขอบผานี้ดู',
  },
  {
    id: 'hidden-master',
    zone: START,
    at: 'spire',
    name: 'ผู้เฒ่าเหยียบเมฆ',
    title: 'นักพรตไร้สำนัก',
    sprite: 'taoist',
    teaches: ['feather-fall'],
    text:
      'ขึ้นมาถึงนี่ได้ ก็ไม่ใช่คนธรรมดา ข้าไม่มีสำนัก มีแต่ลมที่รับข้าไว้ ตำราร่อนลมขนนกอยู่กับนกเพลิงปี้ฟาง ' +
      'เอามาอ่านเรียนเองได้ถึงขั้น 3 แล้วเอามาให้ข้า ข้าพาไปขั้น 4 ขึ้นไป ลองร่อนจากผาลงสู่ศาลเจ้ากลางน้ำโดยไม่แตะพื้น',
  },
];

/** Glyphs of the arts (all already in UI_GLYPHS; listed for the font check). */
export const QINGGONG_GLYPHS = list.map((q) => q.glyph).join('');

/**
 * What a save from before qinggong was learned gets, matching what every character could already
 * do: the dash with the air dash, the qinggong run, three jumps and running up walls onto roofs.
 */
export const LEGACY_QINGGONG: Readonly<Record<string, number>> = {
  'swallow-dash': 3,
  'thousand-li': 3,
  'sky-step': 3,
  'wall-run': 2,
};

/**
 * Falling (the user's decision, 2026-10-07: fall damage is on). A landing from higher than the safe
 * height hurts, more and more steeply, and kills from full health at `lethal` × the safe height.
 * The safe height grows with the qinggong of the air (`FALL_ARTS`: the highest level among them).
 * Heights are metres from where the fall began (the top of the jump, or where a glide or a
 * wall-run let go). A glide eases a fall at `glideBrake` metres a second while it is open, landing
 * in water is always safe, and so is coming down off a flying mount. The numbers are for the user
 * to tune (docs/archive/QINGGONG.md §10).
 */
export const FALL = {
  /** The safe height (metres) by fall level: 0 (no air art) to 5. */
  safe: [8, 12, 16, 20, 25, 30],
  /** At this many safe heights, a fall takes the whole of the health bar. */
  lethal: 2.5,
  /** How steeply the damage grows past the safe height (1: in a straight line). */
  power: 1.6,
  /** An open glide takes this many metres a second off the height a fall counts from. */
  glideBrake: 30,
} as const;

/** The arts whose level sets the fall level (the cloud ascent counts as level 5). */
export const FALL_ARTS: readonly QinggongKind[] = ['skyStep', 'climb', 'glide', 'leap', 'master'];

/**
 * The wall-kick (ไต่กำแพง): Space during a wall-run springs back off the wall and up, and gives one
 * air jump back. Speeds in world pixels (up) and metres (back) a second, the push's seconds, and
 * the stamina it costs.
 */
export const WALL_KICK = { up: 250, back: 9, time: 0.3, stamina: 10 } as const;

/**
 * An updraft (ลมยก): a column of rising air at a cliff's foot, beside a waterfall, round a spire, or
 * over a summit. While gliding inside it (`r` metres round (x, y), below `top` metres above the
 * sea) the fall turns to a climb toward `lift` metres a second, easing off over the last metres to
 * the top, where the glider floats. `x`, `y` are in the map's own metres (a region's for a region,
 * the village's local ones for an authored zone: `systems/wind.ts` adds the core's origin).
 */
export interface Updraft {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly top: number;
  readonly lift: number;
}

/**
 * In the water (systems/water.ts, docs/handoff/newdir/QUEUE.md 3d): wading where it is shallow,
 * swimming where it is deep, diving under, breath (ลมหายใจ), and the currents and waves that push a
 * swimmer. Depths and distances in metres, times in seconds, stamina and health per second.
 * The numbers are for the user to tune.
 */
export const WATER = {
  /** Water this deep or deeper is swum; shallower is waded. */
  swimDepth: 1,
  /** An authored map's sea depth class (`deep` layer, 1 shallow … 3 deep) in metres; 0: no layer. */
  classDepth: [2, 0.6, 1.6, 4],
  /** Walking speed multiplied while wading, swimming and diving. */
  speed: { wade: 0.65, swim: 0.5, dive: 0.4 },
  /** Stamina a second: swimming on, treading water (still), diving. */
  stamina: { swim: 3, tread: 1, dive: 2 },
  /** Seconds of breath (ลมหายใจ) when full, and seconds of it back a second at the surface. */
  breath: 30,
  refill: 10,
  /** Out of breath: this share of the health bar a second, until the swimmer comes up (or drowns). */
  drown: 0.15,
  /** Diving: metres a second down (holding dive) and back up (let go), and the gap kept above the bed. */
  dive: { down: 1.2, up: 2, bed: 0.4 },
  /** A river's push: metres a second for each metre the water falls per metre, and the most it pushes. */
  river: { push: 40, max: 1.6 },
  /**
   * The sea's swell, toward the shallows (where the sea gets shallower): a steady `drift`, and a
   * `surge` on each wave's crest; waves `amp` metres high, `length` metres apart, `period` seconds
   * apart. A function of game time, so it is the same on every machine. Deeper than `calm` metres
   * under, a diver feels a third of it.
   */
  sea: { drift: 0.15, surge: 0.6, amp: 0.35, length: 14, period: 7, calm: 1 },
} as const;

/**
 * Climbing a cliff (systems/climb.ts): a rock face taller than wall-running gets over (ไต่กำแพง's
 * `maxHeight`, or `min` metres without the art) is climbed slowly, hand over hand. `speed` metres a
 * second up, `stamina` a second climbing and `hang` a second holding on; `start` stamina is needed to
 * take hold. Every `ledgeEvery` metres up the face (within `ledgeBand`) there is a ledge where a
 * climber holding still gets `rest` stamina a second back. Out of stamina, the climber falls.
 */
export const CLIMB = {
  min: 2,
  speed: 1.2,
  stamina: 7,
  hang: 2,
  start: 10,
  ledgeEvery: 8,
  ledgeBand: 0.5,
  rest: 12,
} as const;
